'use client';

import { useEffect, useRef, useState, CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import styles from './ScienceSection.module.css';

interface Article {
  eyebrow: string;
  date: string;
  title: string;
  href: string;
  image: string;
  imageAlt: string;
}

const getArticles = (t: (key: string) => string): Article[] => [
  {
    eyebrow: t('photon.scienceSection.eyebrow.newsFeature'),
    date: t('photon.scienceSection.date.natureDate'),
    title: t('photon.scienceSection.title.surprisingScience'),
    href: 'https://www.nature.com/articles/d41586-026-00878-1',
    image: 'web_v2/photon/landing-page/science-nature.png',
    imageAlt: t('photon.scienceSection.image.natureJournal'),
  },
  {
    eyebrow: t('photon.scienceSection.eyebrow.newsFeature'),
    date: t('photon.scienceSection.date.sciamDate'),
    title: t('photon.scienceSection.title.doesRedLightWork'),
    href: 'https://www.scientificamerican.com/article/does-red-light-therapy-work-what-the-research-says/',
    image: 'web_v2/photon/landing-page/science-sciam.png',
    imageAlt: t('photon.scienceSection.image.scientificAmerican'),
  },
];

const ScienceSection = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [cardsVisible, setCardsVisible] = useState(false);
  const t = useTranslations('photon');
  const articles = getArticles(t);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setCardsVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <section className={styles.section} ref={sectionRef}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <h2 className={styles.heading}>
            {t('photon.scienceSection.heading.backedByScience')}
          </h2>
          <p className={styles.description}>
            {t('photon.scienceSection.description.clinicalResearch')}
          </p>
        </div>

        <div className={styles.cardStack}>
          {articles.map((article, i) => (
            <article
              key={i}
              className={styles.card}
              data-visible={cardsVisible}
              style={{ ['--i' as string]: i } as CSSProperties}
            >
              <div className={styles.cardImage}>
                <CustomImage
                  src={article.image}
                  alt={article.imageAlt}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  style={{ objectFit: 'cover' }}
                />
              </div>
              <div className={styles.cardBody}>
                <div className={styles.meta}>
                  <span className={styles.metaPrimary}>{article.eyebrow}</span>
                  <span className={styles.metaSeparator} aria-hidden="true">
                    |
                  </span>
                  <span className={styles.metaDate}>{article.date}</span>
                </div>
                <h3 className={styles.cardTitle}>{article.title}</h3>
                <a
                  className={styles.readMore}
                  href={article.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('photon.scienceSection.link.readMore')}
                  <span className={styles.arrow} aria-hidden="true">
                    ↗
                  </span>
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ScienceSection;
